import gql from 'graphql-tag';

import { Properties, QUESTION_TYPE } from './question.types';

interface BaseQuestion {
  id: number;
  label: string;
  type: QUESTION_TYPE;
  isRequired: boolean;
  sort: number;
  shownBefore: boolean;
}

export type ScaleQuestionType =
  | QUESTION_TYPE.LINEAR_SCALE
  | QUESTION_TYPE.NPS
  | QUESTION_TYPE.SATISFACTION
  | QUESTION_TYPE.RETENTION
  | QUESTION_TYPE.CONNECTION;
type LongType = { type: QUESTION_TYPE.LONG };
type RadioType = { type: QUESTION_TYPE.RADIO };
type CheckType = { type: QUESTION_TYPE.CHECK };
type ScaleType = { type: ScaleQuestionType };

export type LongQuestion = BaseQuestion & LongType;
export type RadioQuestion = BaseQuestion &
  RadioType & {
    options: string[];
    hasOtherOption: boolean;
  };
export type CheckQuestion = BaseQuestion &
  CheckType & {
    options: string[];
    hasOtherOption: boolean;
  };
export type ScaleQuestion = BaseQuestion &
  ScaleType & {
    properties?: Properties;
  };

// todo change name
export type TypedQuestion = LongQuestion | RadioQuestion | CheckQuestion | ScaleQuestion;

export const isLongQuestion = (question: TypedQuestion): question is LongQuestion => {
  return question.type === QUESTION_TYPE.LONG;
};

export const isRadioQuestion = (question: TypedQuestion): question is RadioQuestion => {
  return question.type === QUESTION_TYPE.RADIO;
};

export const isCheckQuestion = (question: TypedQuestion): question is CheckQuestion => {
  return question.type === QUESTION_TYPE.CHECK;
};

export const isScaleQuestion = (question: TypedQuestion): question is ScaleQuestion => {
  return (
    question.type === QUESTION_TYPE.LINEAR_SCALE ||
    question.type === QUESTION_TYPE.NPS ||
    question.type === QUESTION_TYPE.SATISFACTION ||
    question.type === QUESTION_TYPE.CONNECTION ||
    question.type === QUESTION_TYPE.RETENTION
  );
};

export type BaseQuestionResponse = { id: number; type: QUESTION_TYPE };
export type LongQuestionResponse = BaseQuestionResponse &
  LongType & {
    value: string;
  };

export type RadioQuestionResponse = BaseQuestionResponse &
  RadioType &
  ({ selectedOption: string; otherValue: null } | { selectedOption: null; otherValue: string });

export type CheckQuestionResponse = BaseQuestionResponse &
  CheckType & {
    selectedOptions: string[];
    otherValue: string | null;
  };

export type ScaleQuestionResponse = BaseQuestionResponse &
  ScaleType & {
    value: string;
  };

export type QuestionResponse =
  | LongQuestionResponse
  | RadioQuestionResponse
  | CheckQuestionResponse
  | ScaleQuestionResponse;

export const isLongResponse = (response: QuestionResponse): response is LongQuestionResponse => {
  return response.type === QUESTION_TYPE.LONG;
};
export const isRadioResponse = (response: QuestionResponse): response is RadioQuestionResponse => {
  return response.type === QUESTION_TYPE.RADIO;
};
export const isCheckResponse = (response: QuestionResponse): response is CheckQuestionResponse => {
  return response.type === QUESTION_TYPE.CHECK;
};
export const isScaleResponse = (response: QuestionResponse): response is ScaleQuestionResponse => {
  return (
    response.type === QUESTION_TYPE.LINEAR_SCALE ||
    response.type === QUESTION_TYPE.NPS ||
    response.type === QUESTION_TYPE.SATISFACTION ||
    response.type === QUESTION_TYPE.CONNECTION ||
    response.type === QUESTION_TYPE.RETENTION
  );
};

export const QUESTION_RESPONSE_FRAGMENT = gql`
  fragment QuestionResponseFields on QuestionResponse {
    ... on LongResponseType {
      type
      id
      value
    }
    ... on RadioResponseType {
      type
      id
      selectedOption
      otherValue
    }
    ... on CheckResponseType {
      type
      id
      selectedOptions
      otherValue
    }
    ... on ScalarResponseType {
      type
      id
      value
    }
  }
`;

const BASE_QUESTION_FIELDS = `
  id
  label
  type
  isRequired
  sort
  shownBefore
`;

export const QUESTION_FRAGMENT = gql`
  fragment QuestionFields on EventQuestion {
    ... on LongQuestionType {
      ${BASE_QUESTION_FIELDS}
    }
    ... on RadioQuestionType {
      ${BASE_QUESTION_FIELDS}
      options
      hasOtherOption
    }
    ... on CheckQuestionType {
      ${BASE_QUESTION_FIELDS}
      options
      hasOtherOption
    }
    ... on ScalarQuestionType {
      ${BASE_QUESTION_FIELDS}
      properties {
        scaleMinValue
        scaleMaxValue
        scaleMinLabel
        scaleMaxLabel
      }
    }
  }
`;
